import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

/** A single field/operator/value condition over a line's split facts. */
const SplitConditionSchema = z.object({
  field: z.string(),
  operator: z.enum([
    "eq",
    "neq",
    "gt",
    "gte",
    "lt",
    "lte",
    "contains",
    "not_contains",
    "starts_with",
    "ends_with",
    "is_null",
    "is_not_null",
    "in",
    "not_in",
    "regex_match",
  ]),
  value: z.unknown().optional(),
});

/** A named split group: lines matching ALL its conditions split into one draft. */
const SplitRuleSchema = z.object({
  name: z.string(),
  conditions: z.array(SplitConditionSchema),
});

/**
 * Split Draft Sales Order Step (workflow-native approval flow).
 *
 * Declarative, rule-driven split: each line is matched against the ordered
 * `splitRules` (first match wins) over its split facts (line.origin,
 * line.warehouse, line.productGroup, variant.activeInZhuhai, variant.prodview,
 * …); lines matching a rule are grouped into a separate draft named after the
 * rule, while lines matching no rule stay on the original. Replaces the
 * hardcoded `ship-from-location` strategy — that's now expressible as a rule
 * (`variant.activeInZhuhai eq ORDERABLE`).
 *
 * Backend-only (DB access); handler in
 * backend/src/workflows/steps/order-management/.
 */
export const splitDraftSalesOrderStep = defineStepConfig({
  type: "split_draft_sales_order",
  name: "Split Draft Sales Order",
  description:
    "Splits a draft sales order into multiple orders using configurable line split rules",
  category: "Data Access",
  libraryModule: "OrderManagement",
  inputSchema: z.object({
    draftSalesOrderIds: z
      .array(z.string().uuid())
      .describe("UUIDs of the draft sales orders to potentially split"),
  }),

  outputSchema: z.object({
    draftSalesOrderIds: z
      .array(z.string().uuid())
      .describe(
        "UUIDs of all draft sales orders (originals + any created by splits)",
      ),
    splitCount: z
      .number()
      .int()
      .describe("Number of draft sales orders that were actually split"),
  }),

  configSchema: z.object({
    splitRules: z
      .array(SplitRuleSchema)
      .default([])
      .describe(
        "Ordered named groups; a line splits into a separate draft when it matches a rule's conditions (first match wins). Lines matching no rule stay on the original.",
      ),
    disabled: z
      .boolean()
      .default(false)
      .describe("If true, skip splitting and pass through original drafts"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "2 minutes",
    retry: {
      maximumAttempts: 2,
    },
    nonRetryableErrorTypes: ["ValidationError", "SchemaValidationError"],
  },
});
